// Curated knowledge library. Editing here is the source of truth.
// Tags map to module ids (1..4) so items can be surfaced inline later.

export const CATEGORIES = [
  { id: "video", label: "Videos", icon: "Youtube" },
  { id: "publication", label: "Publications", icon: "FileText" },
  { id: "journal", label: "Journals", icon: "BookMarked" },
  { id: "blog", label: "Blogs", icon: "Rss" },
  { id: "supplement", label: "Supplements", icon: "Pill" },
];

export const RESOURCES = [
  // ---------- Videos (seeded by user) ----------------------------------
  {
    id: "v-kaiish-1",
    type: "video",
    title: "Hormone-Gut Axis — Lecture 1",
    source: "The Kaiish Institute of Functional Medicine",
    author: "Kaiish Institute",
    year: "2024",
    url: "https://youtu.be/0h1AiZdUvWk?si=3sjN8W1KulN8jzxx",
    summary:
      "Functional medicine overview of the hormone-gut axis and the role of the estrobolome in estrogen metabolism.",
    tags: [1, 2],
  },
  {
    id: "v-kaiish-2",
    type: "video",
    title: "Estrobolome, β-Glucuronidase & Women's Health",
    source: "The Kaiish Institute of Functional Medicine",
    author: "Kaiish Institute",
    year: "2024",
    url: "https://youtu.be/xSyPjX3YlFE?si=7vSeAcOqEINM3yE5",
    summary:
      "Deeper dive into how β-glucuronidase activity drives estrogen recirculation and clinical implications.",
    tags: [2, 3],
  },

  // ---------- Publications (peer-reviewed, referenced in the course) ---
  {
    id: "p-plottel-2011",
    type: "publication",
    title: "Microbiome and Malignancy",
    source: "Cell Host & Microbe",
    author: "Plottel CS, Blaser MJ",
    year: "2011",
    url: "https://doi.org/10.1016/j.chom.2011.10.003",
    summary:
      "The original paper that defined the estrobolome — the aggregate of enteric bacterial genes capable of metabolizing estrogens.",
    tags: [2],
  },
  {
    id: "p-kwa-2016",
    type: "publication",
    title:
      "The Intestinal Microbiome and Estrogen Receptor–Positive Female Breast Cancer",
    source: "JNCI: Journal of the National Cancer Institute",
    author: "Kwa M, Plottel CS, Blaser MJ, Adams S",
    year: "2016",
    url: "https://doi.org/10.1093/jnci/djw029",
    summary:
      "Reviews evidence linking gut microbiome composition and β-glucuronidase activity to circulating estrogen and ER-positive breast cancer.",
    tags: [2, 3],
  },
  {
    id: "p-ervin-2019",
    type: "publication",
    title:
      "Gut microbial β-glucuronidases reactivate estrogens as components of the estrobolome",
    source: "Journal of Biological Chemistry",
    author: "Ervin SM, Li H, Lim L, et al.",
    year: "2019",
    url: "https://doi.org/10.1074/jbc.RA119.010950",
    summary:
      "Mechanistic evidence that specific bacterial β-glucuronidases deconjugate estrogen in the gut, increasing enterohepatic recirculation.",
    tags: [2],
  },
  {
    id: "p-baker-2017",
    type: "publication",
    title: "Estrogen–gut microbiome axis: Physiological and clinical implications",
    source: "Maturitas",
    author: "Baker JM, Al-Nakkash L, Herbst-Kralovetz MM",
    year: "2017",
    url: "https://doi.org/10.1016/j.maturitas.2017.06.025",
    summary:
      "Broad clinical review of how the gut microbiome modulates estrogen, relevant to menopause, obesity, and endometriosis.",
    tags: [1, 2, 3],
  },

  // ---------- Journals -------------------------------------------------
  {
    id: "j-frontiers-micro",
    type: "journal",
    title: "Frontiers in Microbiology — Gut Microbiota section",
    source: "Frontiers",
    url: "https://www.frontiersin.org/journals/microbiology/sections/microbial-symbioses",
    summary:
      "Open-access journal section publishing ongoing research on gut microbiota, including estrobolome papers.",
    tags: [2, 3],
  },
  {
    id: "j-pubmed",
    type: "journal",
    title: "PubMed — Estrobolome search",
    source: "NCBI",
    url: "https://pubmed.ncbi.nlm.nih.gov/?term=estrobolome",
    summary:
      "Up-to-date peer-reviewed literature search feed for the estrobolome across journals.",
    tags: [2],
  },

  // ---------- Blogs ----------------------------------------------------
  {
    id: "b-precision-nutrition",
    type: "blog",
    title: "The gut microbiome and women's hormones",
    source: "Precision Nutrition",
    url: "https://www.precisionnutrition.com/research-review-the-gut-microbiome",
    summary:
      "Accessible deep-dive that translates microbiome research into practical nutrition guidance.",
    tags: [3, 4],
  },
  {
    id: "b-lara-briden",
    type: "blog",
    title: "How to treat estrogen dominance",
    source: "Lara Briden, ND — The Period Revolutionary",
    url: "https://www.larabriden.com/the-period-revolutionary-book/",
    summary:
      "Naturopathic clinician's perspective on diet, fiber, and liver-gut support for estrogen balance.",
    tags: [1, 4],
  },

  // ---------- Supplements ---------------------------------------------
  {
    id: "s-dim",
    type: "supplement",
    title: "Diindolylmethane (DIM) for estrogen metabolism",
    source: "Alternative Medicine Review",
    author: "Minich DM, Bland JS",
    year: "2007",
    url: "https://pubmed.ncbi.nlm.nih.gov/18069902/",
    summary:
      "Review of DIM's role in shifting estrogen metabolism toward the protective 2-OH pathway in humans.",
    tags: [4],
  },
  {
    id: "s-calcium-d-glucarate",
    type: "supplement",
    title: "Calcium D-glucarate — β-glucuronidase inhibition",
    source: "Alternative Medicine Review",
    author: "Staff",
    year: "2002",
    url: "https://pubmed.ncbi.nlm.nih.gov/12197785/",
    summary:
      "Mechanistic and clinical review of calcium D-glucarate as a β-glucuronidase inhibitor, relevant to estrogen dominance.",
    tags: [2, 4],
  },
  {
    id: "s-magnesium",
    type: "supplement",
    title: "Magnesium and Women's Health",
    source: "Scientifica",
    author: "Porri D, Biesalski HK, et al.",
    year: "2021",
    url: "https://doi.org/10.1155/2021/5592630",
    summary:
      "Evidence review on magnesium's role as a cofactor in Phase II liver detoxification including estrogen clearance.",
    tags: [4],
  },
  {
    id: "s-probiotics",
    type: "supplement",
    title:
      "Probiotics and estrogen-related health: Lactobacillus & Bifidobacterium",
    source: "Nutrients",
    author: "He S, Li H, Yu Z, et al.",
    year: "2021",
    url: "https://doi.org/10.3390/nu13041266",
    summary:
      "Evidence summary on probiotic strains that support the estrobolome and healthy estrogen metabolism.",
    tags: [4],
  },
];
